import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import { FeesApi } from '@/services/fees.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';

const schema = z.object({
  classId: z.string().min(1, 'Required'),
  sectionId: z.string().optional().or(z.literal('')),
  studentId: z.string().min(1, 'Required'),
  feeStructureId: z.string().min(1, 'Required'),
  academicYearId: z.string().min(1, 'Required'),
  amount: z.string().min(1, 'Required'),
  discountAmount: z.string().optional().or(z.literal('')),
  dueDate: z.string().optional().or(z.literal('')),
  paymentDate: z.string().optional().or(z.literal('')),
  paymentMode: z.string().optional().or(z.literal('')),
  paymentReference: z.string().optional().or(z.literal('')),
  paidAmount: z.string().optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

export function FeeDemandFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const term = useTerminology();
  const [receipt, setReceipt] = useState<{ studentName: string; feeName: string; net: number; paid: number } | null>(null);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      classId: '', sectionId: '', studentId: '', feeStructureId: '', academicYearId: '',
      amount: '', discountAmount: '', dueDate: '', paymentDate: '', paymentMode: '', paymentReference: '', paidAmount: '',
    },
  });

  const classId = watch('classId');
  const sectionId = watch('sectionId');
  const studentId = watch('studentId');

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-fees-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];
  const { data: defaultYear } = useQuery({ queryKey: ['fees', 'default-year'], queryFn: FeesApi.defaultYear });
  useEffect(() => {
    if (!isEdit && defaultYear && !watch('academicYearId')) setValue('academicYearId', defaultYear.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultYear, isEdit]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students } = useQuery({
    queryKey: ['fees', 'students-picker', classId, sectionId],
    queryFn: () => FeesApi.studentsPicker({ classId: classId || undefined, sectionId: sectionId || undefined }),
    enabled: !!classId,
  });

  const { data: feeStructures } = useQuery({
    queryKey: ['fees', 'fee-structures-picker', classId],
    queryFn: () => FeesApi.feeStructuresPicker(classId || undefined),
  });

  const { data: existing } = useQuery({
    queryKey: ['fees', 'demand', id],
    queryFn: () => FeesApi.getDemand(id!),
    enabled: isEdit,
  });
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!isEdit || !existing || seeded) return;
    reset((prev) => ({
      ...prev,
      classId: existing.classId ?? '',
      sectionId: existing.sectionId ?? '',
      studentId: existing.studentId,
      feeStructureId: existing.feeStructureId ?? '',
      academicYearId: existing.academicYearId,
      amount: String(existing.actualAmount),
      discountAmount: existing.discountAmount ? String(Math.abs(existing.discountAmount)) : '',
      dueDate: existing.dueDate ?? '',
    }));
    setSeeded(true);
  }, [isEdit, existing, seeded, reset]);

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const payload = {
        studentId: v.studentId,
        feeStructureId: v.feeStructureId,
        academicYearId: v.academicYearId,
        amount: Number(v.amount),
        discountAmount: v.discountAmount ? Number(v.discountAmount) : undefined,
        dueDate: v.dueDate || undefined,
        paymentDate: v.paymentDate || undefined,
        paymentMode: v.paymentMode || undefined,
        paymentReference: v.paymentReference || undefined,
        paidAmount: v.paidAmount ? Number(v.paidAmount) : undefined,
      };
      return isEdit ? FeesApi.updateDemand(id!, payload) : FeesApi.createDemand(payload);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['fees', 'demands'] });
      qc.invalidateQueries({ queryKey: ['fees', 'report'] });
      if (!isEdit) {
        setReceipt({
          studentName: result.studentName,
          feeName: result.feeStructureName,
          net: result.dmdAmount,
          paid: result.adjustedAmount,
        });
      } else {
        navigate('/fees/demands');
      }
    },
  });

  return (
    <>
      <button
        onClick={() => navigate('/fees/demands')}
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back to demands
      </button>

      <PageHeader
        title={isEdit ? 'Edit demand' : 'Add demand'}
        description="A manually-created demand — Class, Student, Fee, Amount, and an optional inline payment."
      />

      <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-6 pb-24">
        <section className="card p-5">
          <h3 className="mb-4 font-semibold text-slate-900">Demand details</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label={term.level} required error={errors.classId?.message}>
              <Select {...register('classId')} onChange={(e) => { setValue('classId', e.target.value); setValue('sectionId', ''); setValue('studentId', ''); }}>
                <option value="">— Select —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={term.group}>
              <Select {...register('sectionId')} disabled={!classId} onChange={(e) => { setValue('sectionId', e.target.value); setValue('studentId', ''); }}>
                <option value="">All {term.group}</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Student" required error={errors.studentId?.message}>
              <Select {...register('studentId')} disabled={!classId || isEdit}>
                <option value="">— Select —</option>
                {(students ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.admissionNo})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Fee" required error={errors.feeStructureId?.message}>
              <Select {...register('feeStructureId')}>
                <option value="">— Select —</option>
                {(feeStructures ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Session Year" required error={errors.academicYearId?.message}>
              <Select {...register('academicYearId')} disabled={isEdit}>
                <option value="">— Select —</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Amount" required error={errors.amount?.message}>
              <Input type="number" {...register('amount')} />
            </Field>
            <Field label="Discount amount" hint="Positive number — stored as a reduction">
              <Input type="number" {...register('discountAmount')} />
            </Field>
            <Field label="Due date">
              <Input type="date" {...register('dueDate')} />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <h3 className="mb-1 font-semibold text-slate-900">Payment (optional)</h3>
          <p className="mb-4 text-xs text-slate-500">
            Leave blank to create the demand unpaid. If you fill in Date and Mode, the full net amount is recorded as
            paid unless you set a different Paid amount below.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Payment date">
              <Input type="date" {...register('paymentDate')} />
            </Field>
            <Field label="Payment mode">
              <Select {...register('paymentMode')}>
                <option value="">— None —</option>
                <option value="Cash">Cash</option>
                <option value="Online">Online</option>
                <option value="Cheque">Cheque</option>
                <option value="Card">Card</option>
              </Select>
            </Field>
            <Field label="Reference">
              <Input {...register('paymentReference')} />
            </Field>
            <Field label="Paid amount" hint="Defaults to the full net amount if left blank">
              <Input type="number" {...register('paidAmount')} />
            </Field>
          </div>
        </section>

        {save.error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Could not save the demand.</div>
        )}

        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur sm:left-64">
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => navigate('/fees/demands')}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add demand'}
            </button>
          </div>
        </div>
      </form>

      {receipt && (
        <Modal
          open
          onClose={() => {
            setReceipt(null);
            navigate('/fees/demands');
          }}
          title="Demand created"
          description="Save this confirmation now."
          footer={
            <button
              className="btn-primary"
              onClick={() => {
                setReceipt(null);
                navigate('/fees/demands');
              }}
            >
              Done
            </button>
          }
        >
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <Receipt className="h-4 w-4" /> Receipt
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Student</span>
                <span>{receipt.studentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Fee</span>
                <span>{receipt.feeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Net demanded</span>
                <span>₹{receipt.net.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Paid</span>
                <span>₹{receipt.paid.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
